import { FontAwesome5 } from '@expo/vector-icons'
import {
  assets,
  Checkbox,
  DatePicker,
  GeneralService,
  ICDType,
  Input,
  SelectSearch,
  useMobile,
  VerClaimType
} from '@pulseops/common'
import { compareOnlyDate } from '@pulseops/submission/common'
import { pipe } from 'fp-ts/lib/function'
import * as React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { Text } from 'react-native-elements'
import { Panel } from '../../claim-common'
import { ClaimInfoProps } from '../ClaimInfoProps'

export const CriticalIllness = (props: ClaimInfoProps) => {
  const { form, icdName, accidents, setIsCanChangeTab } = props
  const { control, watch, clearErrors, setValue } = form
  const { isMobile } = useMobile()
  const { t, i18n } = useTranslation(['claim', 'verificationClaim'])
  const searchOptions = (inputValue: string) => pipe(GeneralService.ciSearchOptions(inputValue))

  const diagnosisDeleteHandler = (idx: number) => {
    const list = [...watch('criticalIllness.diagnosis')]
    list.splice(idx, 1)
    form.setValue('criticalIllness.diagnosis', list)
  }

  const ciDeleteHandler = (idx: number) => {
    const list = [...watch('criticalIllness.ciName')]
    list.splice(idx, 1)
    form.setValue('criticalIllness.ciName', list)
  }

  const isRelatedToAccident = !!watch('criticalIllness.isRelatedToAccident')

  const onChangeRelatedToAccident = (e: boolean) => {
    if (!e) {
      clearErrors(['criticalIllness.occurrenceDate', 'criticalIllness.accidentName'])
      setValue('criticalIllness.occurrenceDate', null)
      setValue('criticalIllness.accidentName', null)
    }
  }

  return (
    <Panel
      title=""
      breadcrumb={
        <View style={[styles.contentText, styles.row]}>
          <Text style={styles.titleSectionText}>{t('CriticalIllness')}</Text>
          <FontAwesome5 name="angle-right" size={20} color="#B0B0B0" />
          <Text style={styles.titleSectionText}>{t('CriticalIllness')}</Text>
        </View>
      }
      isExand={true}
      isHiddenTitle={false}
      styling={styles.sectionInfo}
    >
      <View style={styles.row}>
        <View style={[isMobile ? styles.colFull : styles.colOneThird, { marginRight: 50 }]}>
          <Controller
            name="criticalIllness.isRelatedToAccident"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, onBlur, value } }) => (
              <Checkbox
                title={t('RelatedToAccident')}
                value={value}
                onBlur={onBlur}
                onChange={(e) => {
                  onChange(e)
                  onChangeRelatedToAccident(e)
                  setIsCanChangeTab(false)
                }}
              />
            )}
          />
        </View>
        {isRelatedToAccident && (
          <>
            <View style={[isMobile ? styles.colFull : styles.colOneThird, { marginRight: 50 }]}>
              <Controller
                name={'criticalIllness.occurrenceDate'}
                control={control}
                rules={{
                  required: {
                    value: isRelatedToAccident,
                    message: t('message:MS020009', { field: t('OccurrenceDate') })
                  },
                  validate: (occurrenceDate) => {
                    const currentDate = new Date()
                    const compareDate = !!occurrenceDate && !compareOnlyDate(occurrenceDate, currentDate)
                    const message = t('message:MS070021')
                    if (compareDate) return message
                    return true
                  }
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <DatePicker
                    label={t('OccurrenceDate')}
                    required={isRelatedToAccident}
                    onChange={(e) => {
                      onChange(e)
                      setIsCanChangeTab(false)
                    }}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                  />
                )}
              />
            </View>
            <View style={[styles.colOneThird]}>
              <Controller
                name="criticalIllness.accidentName"
                control={control}
                rules={{
                  required: {
                    value: isRelatedToAccident,
                    message: t('message:MS020009', { field: t('AccidentName') })
                  }
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <SelectSearch
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    label={t('AccidentName')}
                    required
                    options={
                      accidents?.map((option: VerClaimType) => ({
                        value: option.code,
                        label: `${option.code} - ${i18n.language === 'en' ? option.nameEn : option.nameVi}`
                      })) || []
                    }
                    placeholder={t('common:Select')}
                    onChange={(e) => {
                      onChange(e)
                      setIsCanChangeTab(false)
                    }}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                  />
                )}
              />
            </View>
          </>
        )}
      </View>
      <View style={styles.row}>
        <View style={[isMobile ? styles.colFull : styles.colOneThird, { marginRight: 50 }]}>
          <Controller
            name={'criticalIllness.claimEventDate'}
            control={control}
            defaultValue={new Date()}
            rules={{
              required: {
                value: isRelatedToAccident,
                message: t('message:MS020009', { field: t('ClaimEventDate') })
              },
              validate: (claimEventDate) => {
                const currentDate = new Date()
                const compareDate = !!claimEventDate && !compareOnlyDate(claimEventDate, currentDate)
                const message = t('message:MS070008')
                if (compareDate) return message
                return true
              }
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={t('ClaimEventDate')}
                required
                onChange={(e) => {
                  onChange(e)
                  setIsCanChangeTab(false)
                }}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
              />
            )}
          />
        </View>
        <View style={[isMobile ? styles.colFull : styles.colOneThird, { marginRight: 50 }]}>
          <Controller
            name={'criticalIllness.deathDate'}
            control={control}
            rules={{
              validate: (deathDate) => {
                const currentDate = new Date()
                const compareDate = !!deathDate && !compareOnlyDate(deathDate, currentDate)
                const message = t('message:PSDDEOLTCD')
                if (compareDate) return message
                return true
              }
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                label={t('DeathDate')}
                onChange={(e) => {
                  onChange(e)
                  setIsCanChangeTab(false)
                }}
                alwaysShow
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
              />
            )}
          />
        </View>
      </View>
      <View style={styles.row}>
        <View style={isMobile ? styles.colFull : styles.colThreeFourths}>
          <Controller
            name="criticalIllness.diagnosis"
            defaultValue={[]}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('message:MS020009', { field: t('Diagnosis') })
              },
              pattern: {
                value: /^[^*&$#\\<>|~`!@%^()/';.,:/[/\]\"+=_-]*$/g,
                message: t('message:MS050223')
              }
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <SelectSearch
                label={t('Diagnosis')}
                required
                options={
                  icdName?.map((option: ICDType) => ({
                    value: option.icdcode,
                    label: `${option.icdcode} - ${i18n.language === 'en' ? option.diseaseNameEn : option.diseaseNameVn}`
                  })) || []
                }
                placeholder={t('common:Select')}
                onChange={(e) => {
                  onChange(e)
                  setIsCanChangeTab(false)
                }}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                multiple
                key={watch('criticalIllness.diagnosis').length}
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={[isMobile ? styles.colFull : styles.colThreeFourths]}>
          <View style={styles.tableDiagnosis}>
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.label}>{t('Diagnosis')}</Text>
              </View>
            </View>

            {watch('criticalIllness.diagnosis')?.map((addition, index) => (
              <View style={[styles.row, styles.line]} key={index}>
                <View style={styles.col}>
                  <Text>
                    {addition.value.split(':').length > 1 ? addition.value.split(':')[1] : addition.value.split(':')[0]}
                  </Text>
                </View>
                <View style={styles.col}>
                  <Text>
                    {addition.label.split(':').length > 1 ? addition.label.split(':')[1] : addition.label.split(':')[0]}
                  </Text>
                </View>
                <View style={[styles.col, { alignItems: 'flex-end' }]}>
                  <TouchableOpacity onPress={() => diagnosisDeleteHandler(index)}>
                    <assets.DeleteIcon />
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View style={styles.row}>
        <View style={isMobile ? styles.colFull : styles.colThreeFourths}>
          <Controller
            name="criticalIllness.ciName"
            defaultValue={[]}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('message:MS020009', { field: t('CriticalIllnessName') })
              },
              pattern: {
                value: /^[^*&$#\\<>|~`!@%^()/';.,:/[/\]\"+=_-]*$/g,
                message: t('message:MS050223')
              }
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <SelectSearch
                label={t('CriticalIllnessName')}
                required
                searchOptions={searchOptions}
                placeholder={t('common:Select')}
                onChange={(e) => {
                  onChange(e)
                  setIsCanChangeTab(false)
                }}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                multiple
                key={watch('criticalIllness.ciName').length}
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={[isMobile ? styles.colFull : styles.colThreeFourths]}>
          <View style={styles.tableDiagnosis}>
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.label}>{t('CriticalIllnessName')}</Text>
              </View>
            </View>

            {watch('criticalIllness.ciName')?.map((addition, index) => (
              <View style={[styles.row, styles.line]} key={index}>
                <View style={styles.col}>
                  <Text>{addition.value}</Text>
                </View>
                <View style={styles.col}>
                  <Text>{addition.label.split(' - ')[1]}</Text>
                </View>
                <View style={[styles.col, { alignItems: 'flex-end' }]}>
                  <TouchableOpacity onPress={() => ciDeleteHandler(index)}>
                    <assets.DeleteIcon />
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>
      <View style={styles.row}>
        <View style={[styles.colFull, { paddingHorizontal: 0 }]}>
          <Controller
            name="criticalIllness.remark"
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: `${t('message:MaxLength', { field: '500' })}`
              }
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={t('Remark')}
                onChange={(e) => {
                  onChange(e)
                  setIsCanChangeTab(false)
                }}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                maxLength={500}
                multiline
                numberOfLines={5}
                inputStyle={{ backgroundColor: '#FFFFFF' }}
              />
            )}
          />
        </View>
      </View>
    </Panel>
  )
}

const styles = StyleSheet.create({
  sectionInfo: {
    // marginTop: 20,
    padding: 30,
    width: '100%',
    backgroundColor: '#FAFAFA',
    borderStartWidth: 1,
    borderEndWidth: 1,
    borderBottomWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D3DCE6',
    borderBottomStartRadius: 8,
    borderBottomEndRadius: 8,
    marginBottom: 16
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  colFull: {
    width: '100%',
    paddingHorizontal: 16,
    marginBottom: 32
  },
  col: {
    flex: 1 / 3,
    marginBottom: 12
  },
  colOneThird: {
    flex: 1 / 3,
    marginBottom: 32
  },
  colThreeFourths: {
    flex: 2 / 3,
    marginBottom: 32
  },
  label: {
    color: '#70777E',
    fontSize: 15,
    fontWeight: 'bold',
    lineHeight: 20,
    marginBottom: 8
  },
  value: {
    color: '#000000',
    fontSize: 15,
    lineHeight: 22
  },
  contentText: {
    padding: 5,
    borderRadius: 20,
    width: 'fit-content',
    backgroundColor: '#E5EAEF',
    marginBottom: 26
  },
  titleSectionText: {
    fontSize: 13,
    lineHeight: 22,
    fontWeight: '600',
    paddingStart: 10,
    paddingEnd: 10,
    color: '#4F4F4F'
  },
  tableDiagnosis: {
    // marginTop: 20,
    padding: 30,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#D3DCE6',
    borderRadius: 8,
    marginBottom: 16
  },
  line: {
    width: '100%',
    paddingBottom: 10,
    marginBottom: 22,
    borderBottomWidth: 1,
    borderBottomColor: '#D3DCE6'
  }
})
